import React from 'react';
import { 
  Box, 
  Typography, 
  Card, 
  CardContent, 
  Button, 
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip
} from '@mui/material';
import { styled, useTheme } from '@mui/material/styles';
import EditIcon from '@mui/icons-material/Edit';
import { PageContainer, PageHeader } from 'app/components/StyledComponents';
import { BreadcrumbsComponent } from 'app/components/BreadcrumbsComponent';

const MainContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(3),
  height: "100%",
  maxWidth: '1200px',
  margin: '0 auto',
  padding: theme.spacing(3)
}));

const StyledCard = styled(Card)(({ theme }) => ({
  borderRadius: theme.spacing(1.5),
  backgroundColor: theme.palette.background.paper,
  overflow: 'hidden',
  position: 'relative',
  boxShadow: theme.palette.mode === 'dark' 
    ? '0 2px 8px rgba(0, 0, 0, 0.3)' 
    : '0 2px 8px rgba(15, 40, 76, 0.05)',
  border: `1px solid ${theme.palette.mode === 'dark' ? theme.palette.grey[800] : theme.palette.grey[200]}`,
}));

const SectionHeader = styled(Typography)(({ theme }) => ({
  fontSize: '1.5rem',
  fontWeight: 600,
  color: theme.palette.primary.main,
  marginBottom: theme.spacing(2)
}));

const StyledTable = styled(Table)(({ theme }) => ({
  '& .MuiTableHead-root': {
    backgroundColor: theme.palette.mode === 'dark' ? theme.palette.grey[800] : theme.palette.grey[50],
  },
  '& .MuiTableHead-root .MuiTableCell-root': {
    backgroundColor: theme.palette.mode === 'dark' ? theme.palette.grey[800] : theme.palette.grey[50],
    color: theme.palette.text.primary,
    fontWeight: 600,
    fontSize: '0.875rem',
    borderBottom: `1px solid ${theme.palette.mode === 'dark' ? theme.palette.grey[700] : theme.palette.grey[200]}`,
  },
  '& .MuiTableBody-root .MuiTableCell-root': {
    borderBottom: `1px solid ${theme.palette.mode === 'dark' ? theme.palette.grey[800] : theme.palette.grey[100]}`,
    fontSize: '0.875rem',
    color: theme.palette.text.primary,
  },
  '& .MuiTableRow-root:hover': {
    backgroundColor: theme.palette.mode === 'dark' ? theme.palette.grey[900] : theme.palette.grey[50],
  }
}));

const SkillGrid = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
  gap: theme.spacing(2),
  padding: theme.spacing(3),
}));

const SkillItem = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
  fontSize: '0.875rem',
  padding: theme.spacing(1),
  backgroundColor: theme.palette.mode === 'dark' ? theme.palette.grey[800] : theme.palette.grey[50],
  borderRadius: theme.spacing(0.5),
  textAlign: 'center',
}));

export default function ConfigurePage() {
  const theme = useTheme();

  const breadcrumbsList = [
    { label: "My Agents", isActive: true }
  ];

  return (
    <Box sx={{
      height: 'calc(100vh - 66px)',
      padding: '0 48px',
      display: 'flex',
      flexDirection: 'column'
    }}>
      <Box sx={{ marginTop: '48px', marginBottom: '48px' }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
          {/* <BreadcrumbsComponent breadcrumbsList={breadcrumbsList} /> */}
          <PageHeader sx={{ marginBottom: '0 !important' }}>Agent Configurations</PageHeader>
        </Box>
      </Box>
      
      <MainContainer>
        {/* Page Description */}
        <Box sx={{ mb: 4 }}>
          <Typography 
            variant="body1" 
            color="text.secondary"
            sx={{ fontSize: '1.125rem', lineHeight: 1.6 }}
          >
            Empower your dealership with intelligent agents that automate workflows or assist as co-pilots
          </Typography>
        </Box>

        {/* Always-On Agents Table */}
        <Box>
          <SectionHeader>Auto Pilot</SectionHeader>
          <StyledCard>
            <StyledTable>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: '25%' }}>Agent</TableCell>
                  <TableCell sx={{ width: '40%' }}>Description</TableCell>
                  <TableCell sx={{ width: '20%' }}>Criteria</TableCell>
                  <TableCell sx={{ width: '15%', textAlign: 'center' }}>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>Unsold Nurturing</TableCell>
                  <TableCell sx={{ color: 'text.secondary' }}>Automatically nurture your unsold buyers</TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body2">30 days</Typography>
                      <Tooltip title="Edit criteria">
                        <IconButton size="small" disabled sx={{ opacity: 0.5 }}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                  <TableCell sx={{ textAlign: 'center' }}>
                    <Switch checked disabled sx={{ opacity: 0.5 }} />
                  </TableCell>
                </TableRow>
              </TableBody>
            </StyledTable>
          </StyledCard>
        </Box>

        {/* Added Agents Table */}
        <Box>
          <SectionHeader>Added</SectionHeader>
          <StyledCard>
            <StyledTable>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: '25%' }}>Agent</TableCell>
                  <TableCell sx={{ width: '40%' }}>Description</TableCell>
                  <TableCell sx={{ width: '20%' }}>Criteria</TableCell>
                  <TableCell sx={{ width: '15%', textAlign: 'center' }}>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>Sold Nurturing</TableCell>
                  <TableCell sx={{ color: 'text.secondary' }}>
                    Nurture your sold customers to become repeat buyers
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body2">2016–2022</Typography>
                      <Tooltip title="Edit criteria">
                        <IconButton size="small">
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                  <TableCell sx={{ textAlign: 'center' }}>
                    <Switch 
                      checked 
                      sx={{ 
                        '& .MuiSwitch-switchBase.Mui-checked': {
                          color: '#10b981',
                        },
                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                          backgroundColor: '#10b981',
                        },
                      }} 
                    />
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>Reporting Co-Pilot</TableCell>
                  <TableCell sx={{ color: 'text.secondary' }}>
                    Get answers to any question regarding your dealership data by asking Mia
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body2">CDP-lite</Typography>
                      <Tooltip title="Edit criteria">
                        <IconButton size="small">
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                  <TableCell sx={{ textAlign: 'center' }}>
                    <Switch 
                      checked 
                      sx={{ 
                        '& .MuiSwitch-switchBase.Mui-checked': {
                          color: '#10b981',
                        },
                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                          backgroundColor: '#10b981',
                        },
                      }} 
                    />
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>Ad-hoc Campaigns Co-Pilot</TableCell>
                  <TableCell sx={{ color: 'text.secondary' }}>
                    Generate ad-hoc campaigns targeting specific buyer profiles for the dealership
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body2">All buyer profiles</Typography>
                      <Tooltip title="Edit criteria">
                        <IconButton size="small">
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                  <TableCell sx={{ textAlign: 'center' }}>
                    <Switch 
                      checked 
                      sx={{ 
                        '& .MuiSwitch-switchBase.Mui-checked': {
                          color: '#10b981',
                        },
                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                          backgroundColor: '#10b981',
                        },
                      }} 
                    />
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>Generate Emails Co-Pilot</TableCell>
                  <TableCell sx={{ color: 'text.secondary' }}>
                    Generate lease, finance, or vehicle highlight emails in seconds
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body2">All campaigns</Typography>
                      <Tooltip title="Edit criteria">
                        <IconButton size="small">
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                  <TableCell sx={{ textAlign: 'center' }}>
                    <Switch 
                      checked 
                      sx={{ 
                        '& .MuiSwitch-switchBase.Mui-checked': {
                          color: '#10b981',
                        },
                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                          backgroundColor: '#10b981',
                        },
                      }} 
                    />
                  </TableCell>
                </TableRow>
              </TableBody>
            </StyledTable>
          </StyledCard>
        </Box>

        {/* Coming Soon Agents Table */}
        <Box>
          <SectionHeader>Coming Soon</SectionHeader>
          <StyledCard>
            <StyledTable>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: '25%' }}>Agent</TableCell>
                  <TableCell sx={{ width: '40%' }}>Description</TableCell>
                  <TableCell sx={{ width: '20%' }}>Criteria</TableCell>
                  <TableCell sx={{ width: '15%', textAlign: 'center' }}>Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>Service Reminder Agent</TableCell>
                  <TableCell sx={{ color: 'text.secondary' }}>
                    Reach out to customers due for maintenance with customized messages based on their vehicle history
                  </TableCell>
                  <TableCell>Customizable</TableCell>
                  <TableCell sx={{ textAlign: 'center' }}>
                    <Button 
                      variant="contained" 
                      sx={{ 
                        backgroundColor: '#3b82f6',
                        '&:hover': {
                          backgroundColor: '#2563eb',
                        },
                        textTransform: 'none',
                        fontWeight: 500,
                        px: 2,
                        py: 0.5
                      }}
                    >
                      Sign Up for Updates
                    </Button>
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>Declined RO Agent</TableCell>
                  <TableCell sx={{ color: 'text.secondary' }}>
                    Follow up on declined service estimates with relevant content and financing options
                  </TableCell>
                  <TableCell>Real-time</TableCell>
                  <TableCell sx={{ textAlign: 'center' }}>
                    <Button 
                      variant="contained" 
                      sx={{ 
                        backgroundColor: '#3b82f6',
                        '&:hover': {
                          backgroundColor: '#2563eb',
                        },
                        textTransform: 'none',
                        fontWeight: 500,
                        px: 2,
                        py: 0.5
                      }}
                    >
                      Sign Up for Updates
                    </Button>
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>F&I Renewal Agent</TableCell>
                  <TableCell sx={{ color: 'text.secondary' }}>
                    Reach out to customers with expiring warranties and protection products
                  </TableCell>
                  <TableCell>30-60 days</TableCell>
                  <TableCell sx={{ textAlign: 'center' }}>
                    <Button 
                      variant="contained" 
                      sx={{ 
                        backgroundColor: '#3b82f6',
                        '&:hover': {
                          backgroundColor: '#2563eb',
                        },
                        textTransform: 'none',
                        fontWeight: 500,
                        px: 2,
                        py: 0.5
                      }}
                    >
                      Sign Up for Updates
                    </Button>
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>Lease Return Agent</TableCell>
                  <TableCell sx={{ color: 'text.secondary' }}>
                    Contact customers with leases expiring soon and get them into their new vehicle showcasing
                    relevant new inventory
                  </TableCell>
                  <TableCell>60-90 days</TableCell>
                  <TableCell sx={{ textAlign: 'center' }}>
                    <Button 
                      variant="contained" 
                      sx={{ 
                        backgroundColor: '#3b82f6',
                        '&:hover': {
                          backgroundColor: '#2563eb',
                        },
                        textTransform: 'none',
                        fontWeight: 500,
                        px: 2,
                        py: 0.5
                      }}
                    >
                      Sign Up for Updates
                    </Button>
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>Trade-In Agent</TableCell>
                  <TableCell sx={{ color: 'text.secondary' }}>
                    Reach out to past customers with current vehicles in the desired vehicle acquisition target range
                  </TableCell>
                  <TableCell>3-5 years</TableCell>
                  <TableCell sx={{ textAlign: 'center' }}>
                    <Button 
                      variant="contained" 
                      sx={{ 
                        backgroundColor: '#3b82f6',
                        '&:hover': {
                          backgroundColor: '#2563eb',
                        },
                        textTransform: 'none',
                        fontWeight: 500,
                        px: 2,
                        py: 0.5
                      }}
                    >
                      Sign Up for Updates
                    </Button>
                  </TableCell>
                </TableRow>
              </TableBody>
            </StyledTable>
          </StyledCard>
        </Box>

        {/* Skill Agents Section */}
        <Box sx={{ pb: 4 }}>
          <SectionHeader>Skill Agents</SectionHeader>
          <StyledCard>
            <SkillGrid>
              <SkillItem>Identify Offers</SkillItem>
              <SkillItem>Get Latest Inventory Information and Availability</SkillItem>
              <SkillItem>Identify Deals</SkillItem>
              <SkillItem>Identify Distressed Inventory</SkillItem>
              <SkillItem>Generate Lease Offer Message</SkillItem>
              <SkillItem>Generate Finance Offer Message</SkillItem>
              <SkillItem>Generate New Vehicle Highlight Message</SkillItem>
              <SkillItem>Generate Used Vehicle Highlight Message</SkillItem>
              <SkillItem>Generate Private Offer Message</SkillItem>
              <SkillItem>Generate Distressed Inventory Message</SkillItem>
              <SkillItem>Identify Audience</SkillItem>
              <SkillItem>Optimize Delivery</SkillItem>
              <SkillItem>Establish Cadence</SkillItem>
              <SkillItem>Optimize and Understand User Analysis Intent</SkillItem>
            </SkillGrid>
          </StyledCard>
        </Box>
      </MainContainer>
    </Box>
  );
}
